// Web-only callers persist approved sends in this tab before making a POST.
// Keep ambiguous requests (and acknowledged receipts) until the chat store
// adopts them. Reloads/retries use the same immutable request and UUID.
const PREFIX = 'gab-work-submissions:v1:';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RETRY_MS = 10000;
const canonical = value => JSON.stringify(value, (_key, item) => item && typeof item === 'object' && !Array.isArray(item)
  ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
const transient = error => !error?.status || error.status >= 500 || error.status === 404
  || error.status === 409 && error.data?.reason === 'work_session_starting';
const changed = () => Object.assign(new Error('Your account changed. Open Work again.'), { status: 401, data: { silent: true } });

export function createWorkLaunchSubmissions({ storage, getAccount, send, uuid = () => globalThis.crypto.randomUUID(), now = Date.now }) {
  const inFlight = new Map();
  function load(owner) {
    let rows;
    try { rows = JSON.parse(storage().getItem(PREFIX + owner) || '[]'); }
    catch (_) { throw new Error('Could not read your saved Work requests. Restore browser storage before retrying.'); }
    if (!Array.isArray(rows) || rows.some(row => !UUID.test(row?.id) || !row.input || typeof row.input !== 'object'))
      throw new Error('Could not read your saved Work requests. Restore browser storage before retrying.');
    return rows;
  }
  function save(owner, rows) {
    try { storage().setItem(PREFIX + owner, JSON.stringify(rows)); }
    catch (_) { throw new Error('Could not save your Work request in this tab. Check browser storage, then try again.'); }
  }
  function update(owner, id, patch) {
    const rows = load(owner);
    save(owner, rows.map(row => row.id === id ? { ...row, ...patch } : row));
  }
  function account() {
    const current = getAccount();
    if (!current?.id || !current.authenticated) throw changed();
    return current;
  }
  function currentGuard(owner, extra) {
    return () => {
      const current = getAccount();
      return current?.authenticated && current.id === owner.id && current.generation === owner.generation && (!extra || extra());
    };
  }
  function perform(owner, row, extra) {
    const key = `${owner.id}:${row.id}`;
    if (inFlight.has(key)) return inFlight.get(key);
    const current = currentGuard(owner, extra);
    const promise = (async () => {
      if (!current()) throw changed();
      if (row.receipt) return row.receipt;
      update(owner.id, row.id, { retryAt: now() + RETRY_MS, paused: false });
      try {
        const receipt = await send({ ...row.input, launchSubmission: { version: 1, id: row.id } }, current);
        if (!current()) throw changed();
        if (receipt?.launchQueue?.version !== 1 || !receipt.id || !receipt.conversationId || receipt.error === true)
          throw Object.assign(new Error('Could not confirm your saved Work request.'), { status: 502 });
        update(owner.id, row.id, { receipt });
        return receipt;
      } catch (error) {
        if (!current()) throw changed();
        const retry = transient(error);
        update(owner.id, row.id, { retryAt: now() + RETRY_MS, paused: !retry });
        if (retry) throw Object.assign(new Error('Your request is saved in this tab. We’ll confirm it when the connection recovers.'),
          { status: error.status || 0, data: { reason: 'work_submission_pending' } });
        throw error;
      }
    })().finally(() => inFlight.delete(key));
    inFlight.set(key, promise);
    return promise;
  }
  async function submit(input, { isRequestCurrent } = {}) {
    const owner = account();
    if (!currentGuard(owner, isRequestCurrent)()) throw changed();
    const snapshot = JSON.parse(JSON.stringify(input)), rows = load(owner.id), fingerprint = canonical(snapshot);
    let row = rows.find(candidate => canonical(candidate.input) === fingerprint);
    if (!row) {
      row = { id: uuid(), input: snapshot, createdAt: now(), retryAt: now() + RETRY_MS };
      if (!UUID.test(row.id)) throw new Error('Could not create a secure Work submission identity.');
      save(owner.id, [...rows, row]);
    }
    return perform(owner, row, isRequestCurrent);
  }
  async function recover() {
    const owner = account(), recovered = [];
    for (const row of load(owner.id)) {
      if (!currentGuard(owner)()) break;
      if (inFlight.has(`${owner.id}:${row.id}`) || !row.receipt && (row.paused || row.retryAt > now())) continue;
      try {
        const receipt = await perform(owner, row);
        if (currentGuard(owner)()) recovered.push({ receipt, input: row.input });
      } catch (_) { /* Keep its exact identity; explicit refusals wait for a manual retry. */ }
    }
    return recovered;
  }
  function acknowledge(receipt) {
    const owner = account();
    save(owner.id, load(owner.id).filter(row => row.receipt?.id !== receipt.id));
  }
  return { submit, recover, acknowledge };
}
